import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { createCommandRegistry, filterCommands } from "@/lib/commandRegistry";
import { Search } from "lucide-react";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Get command registry
  const commandGroups = createCommandRegistry(navigate);
  const filteredGroups = filterCommands(commandGroups, search);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Close on navigation
  useEffect(() => {
    setOpen(false);
    setSearch("");
  }, [location.pathname]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Type a command or search..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <Search className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No results found</p>
          </div>
        </CommandEmpty>

        {filteredGroups.map((group) => (
          <CommandGroup key={group.label} heading={group.label}>
            {group.commands.map((command) => {
              const Icon = command.icon;
              const isCurrentPage = location.pathname === command.page;

              return (
                <CommandItem
                  key={command.id}
                  onSelect={() => {
                    command.action();
                    setOpen(false);
                  }}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span>{command.label}</span>
                        {isCurrentPage && (
                          <span className="text-xs text-muted-foreground">
                            (current)
                          </span>
                        )}
                      </div>
                      {command.description && (
                        <p className="text-xs text-muted-foreground">
                          {command.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {command.shortcut && (
                    <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                      {command.shortcut}
                    </kbd>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
